import { useMemo, useState } from "react";
import { Table } from "reactstrap";
import { IAddressBusinessEntity } from "../../../../types/address";
import { Pageable } from "../../../../types/common";
import { Action } from "../../../../types/reducer-action";
import { capitalize } from "../../../../utils/commons";
import { DataEntryEvent } from "../../../common/data-entry-reducer";
import OmsToolTip from "../../../common/oms-tooltip/oms-tooltip";
import PaginationBar from "../../../common/pagination-bar";
import TableHeaders, { TableHeaderInfo } from "../../../common/table-header";
import { AddressManagerDomain, useAddressDispatch, useAddressState } from "../address-manager-context";
import TableRow from "../../../common/table-row";

interface BusinessEntityInfo extends IAddressBusinessEntity {
  location: string;
}

const TABLE_HEADERS: TableHeaderInfo<BusinessEntityInfo>[] = [
  { header: 'Floor/Unit/Room/Suite', accessor: 'location', sortable: true },
  { header: 'Name/Title', accessor: 'entityName', sortable: true },
  { header: 'Entity', accessor: 'entityType', sortable: true }
];

const BusinessEntityTable = (props: Pageable) => {
  const { pageSize } = props;

  const { subdomains } = useAddressState();
  const dispatch = useAddressDispatch();

  const { [AddressManagerDomain.BusinessEntities]: businessEntityState } = subdomains;
  const { dataSet, selectedDataId } = businessEntityState ?? {};

  const formattedData = useMemo(() => (
    dataSet?.map(item => ({
      ...item,
      location: item.level ?? item.unit
    }) as BusinessEntityInfo)
  ), [dataSet]);

  const [sortedData, setSortedData] = useState<BusinessEntityInfo[]>([]);
  const [pagedData, setPagedData] = useState<BusinessEntityInfo[]>([]);

  const selectItem = (item: IAddressBusinessEntity) => dispatch(
    new Action(AddressManagerDomain.BusinessEntities + DataEntryEvent.SelectData, item.id)
  );

  return (
    <section aria-label="Search results">
      <Table striped hover className="fixed" id="business-entities">
        <TableHeaders headers={TABLE_HEADERS} sortableData={formattedData} onSort={setSortedData} />

        {dataSet && dataSet.length > 0
          ?
          <tbody>
            {pagedData.map((item) =>
              <TableRow key={item.id}
                selected={item.id === selectedDataId} onSelect={() => selectItem(item)}>
                <td>{item.location}</td>
                <td>
                  <OmsToolTip
                    orgId={item.entityType === 'organization' ? item.entityId : 0}
                    positionId={item.entityType === 'position' ? item.entityId : 0}>
                    {item.entityName}
                  </OmsToolTip>
                </td>
                <td>{capitalize(item.entityType)}</td>
              </TableRow>
            )}
          </tbody>
          :
          <tfoot>
            <tr>
              <td colSpan={TABLE_HEADERS.length} className="text-center">
                {dataSet?.length === 0
                  ? 'No business entities were found associated with the address.'
                  : 'Results will appear in this table.'
                }
              </td>
            </tr>
          </tfoot>
        }
      </Table>

      <PaginationBar data={sortedData} pageSize={pageSize} onPageChange={setPagedData} />
    </section>
  );
};

export default BusinessEntityTable;
